/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { ToastType } from '@components/Toast/Toast';
import { cloneStopFacilityDetails } from '@common/facilityDetails';
import { copiedAppointmentFields } from '@common/stops/appointmentScheduling';
import { FeatureFlags } from '@constants/featureFlags';
import {
  resetLtlReferenceNumberStore,
  resetQuoteParametersStore,
} from '@store/ltl/store';
import {
  FalveyInsuranceQuoteInLoad,
  LTLAddressBookSnapshot,
  TempQuoteModel,
} from '@store/ltl/types';
import { userStore } from '@store/user';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { Address } from '@typeDefinitions/addressTypes';
import { VendorLineItem } from '@typeDefinitions/lineItemTypes';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { OperationTypeV4 } from '@typeDefinitions/operationTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { VendorContractViewModel } from '@typeDefinitions/vendorTypes';
import { BuildOrderFromQuote } from '@utils/ltl';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';
import { formatAmount, handleToast } from '@utils/utils';
import size from 'lodash/size';
import { DateTime } from 'luxon';

import { updateOrderState } from './actions/orderFormActions';
import {
  addNewVendorToOrder,
  updateVendorPropertyAtIndex,
} from './actions/vendorActions';
import { falveyVendorInitialState } from './constant';
import { load, stops } from './selectors';
import { orderStore, setOrderStore } from './store';

/**
 * This function copies a load.
 * @param load The load to copy
 * @param perserveCarrier Whether to preserve the carrier
 * @param perserveStopDateAndTime If this is false, then the stop date will be empty
 * @param advanceOneDay If this is true, then the stop date will advance by one day
 * @param clearCarrierForTlModes If true, TL/Partial clear carrier, status, pay and rates
 * @returns The copied load
 */
// eslint-disable-next-line complexity
export const copyLoad = (
  load: ILoadEditViewModel,
  perserveCarrier?: boolean,
  perserveStopDateAndTime?: boolean,
  advanceOneDay = false,
  clearCarrierForTlModes = false,
) => {
  let newStopId = 0;
  let newItemId = 0;
  const isLTL = load.mode === 'LTL';
  if (isLTL) {
    resetLtlReferenceNumberStore();
    resetQuoteParametersStore();
  }
  const keepCarrier = perserveCarrier !== undefined ? perserveCarrier : false;
  const keepStopDateAndTime =
    perserveStopDateAndTime !== undefined ? perserveStopDateAndTime : false;
  const clearForTlMode =
    clearCarrierForTlModes && ['TL', 'Partial'].includes(load.mode ?? '');
  const clearCarrier = (!keepCarrier && isLTL) || clearForTlMode;
  return {
    loadInformation: clearCarrier ? undefined : load.loadInformation,
    warnings: load.warnings,
    sequence: load.sequence,
    type: load.type,
    customerReferenceNumber: load.customerReferenceNumber,
    carrierName: clearCarrier ? undefined : load.carrierName,
    carrierId: clearCarrier ? undefined : load.carrierId,
    carrierActionPlan: clearCarrier ? undefined : load.carrierActionPlan,
    carrierStatus: clearCarrier ? 'Unassigned' : load.carrierStatus,
    carrierContactId: clearCarrier ? undefined : load.carrierContactId,
    equipment: load.equipment,
    mode: load.mode,
    teamRequired: load.teamRequired,
    hazmatRequired: load.hazmatRequired,
    tarpRequired: load.tarpRequired,
    hideCustomerName: load.hideCustomerName,
    hasBeenSharedWithCapacity: false,
    freightValue: undefined,
    carrierInstructions:
      clearCarrier && !clearForTlMode ? undefined : load.carrierInstructions,
    carrierMC: clearCarrier ? undefined : load.carrierMC,
    terminal: isLTL ? undefined : load.terminal,
    practicalMileage: load.practicalMileage,
    shortestMileage: load.shortestMileage,
    driver: undefined,
    driverPhoneNumber: undefined,
    truckNumber: undefined,
    trailerNumber: undefined,
    vinNumber: undefined,
    trackingNumber: undefined,
    product: load.product,
    tempInstructions: load.tempInstructions,
    minTemperature: load.minTemperature,
    maxTemperature: load.maxTemperature,
    temperatureUnit: load.temperatureUnit,
    runMode: load.runMode,
    enableLoadCarrierRestrictionOverride: false,
    enableLoadCarrierRestrictionOverrideOffice: undefined,
    billingHold: false,
    billingHoldReason: undefined,
    billingHoldDescription: undefined,
    billedDate: undefined,
    paidDate: undefined,
    trackingEnabled: false,
    assignedToUserId: load.assignedToUserId,
    assignedToGroupId: undefined,
    assignedToUser: load.assignedToUser,
    assignedToGroup: undefined,
    assignedToOffice: load.assignedToOffice,
    legalText: load.legalText,
    profitSplit: load.profitSplit,
    sharedWithOfficeId: undefined,
    od: load.od,
    nmfc: load.nmfc,
    weight: load.weight,
    marketRate: load.marketRate,
    class: load.class,
    canRate: load.canRate,
    synched: false,
    edi: load.edi,
    minPrice: clearForTlMode ? undefined : load.minPrice,
    maxPrice: clearForTlMode ? undefined : load.maxPrice,
    lastInternalNote: undefined,
    postingId: null,
    signedRateCon: false,
    signedConfirmationDate: undefined,
    customerId: load.customerId,
    ramps: load.ramps,
    macropointInterval: undefined,
    ltlSubCarrier: null,
    tenderedToTransfloDate: undefined,
    currencyType: load.currencyType,
    canVoidLoad: true,
    assignedId: 0,
    owner: load.owner,
    carrierContact: clearCarrier ? undefined : load.carrierContact,
    claim: null,
    trackingService: undefined,
    operationType: 'Insert',
    id: 0,
    orderId: 0,
    loadNum: '',
    quoteID: null,
    isCopied: true,
    ltlQuoteId: null,
    ltlQuote: undefined,
    quoteItems: [],
    quoteStops: [],
    enableTracking: false,
    editable: true,
    status:
      !clearCarrier && Boolean(load.carrierId) ? 'Assigned' : 'Unassigned',
    useStatusStateMachine: true,
    coveredBy: userStore.user.name,
    coveredById: userStore.user.id,
    payments: [],
    comments: [],
    flags: [],
    lineItems: clearForTlMode
      ? [
          {
            operationType: 'Insert' as OperationTypeV4,
            id: 0,
            orderId: 0,
            settledDate: null,
            status: 'Unsettled',
            readOnly: false,
            synched: false,
            pickUpId: undefined,
            dropOffId: undefined,
            type: 'Line Haul',
            description: '',
            createdDate: new Date(),
            quantity: 1,
            rate: 0,
            estimate: false,
            adjustment: false,
            referenceNumber: '',
            stopId: undefined,
            descriptionWithParanthesis: '',
            rateDetail: '',
          },
        ]
      : isLTL
        ? []
        : load.lineItems
            ?.filter((lineItem) => lineItem.operationType !== 'Delete')
            .map((lineItem) => ({
              operationType: 'Insert' as OperationTypeV4,
              id: 0,
              orderId: 0,
              settledDate: null,
              status: 'Unsettled',
              readOnly: false,
              synched: false,
              pickUpId: undefined,
              dropOffId: undefined,
              type: lineItem.type,
              description: lineItem.description,
              createdDate: new Date(),
              quantity: lineItem.quantity,
              rate: lineItem.rate,
              estimate: lineItem.estimate,
              adjustment: lineItem.adjustment,
              referenceNumber: lineItem.referenceNumber,
              stopId: undefined,
              descriptionWithParanthesis: lineItem.descriptionWithParanthesis,
              rateDetail: lineItem.rateDetail,
            }))
            .filter((item) => item.type !== 'Adjustment'),
    stops: load.stops
      ?.filter((stop) => stop.operationType !== 'Delete')
      .map((stop) => {
        newStopId--;

        let newStopTime: DateTime | undefined = undefined;

        if (keepStopDateAndTime) {
          newStopTime = DateTime.fromISO(stop.stopDateTime ?? '');
        } else if (Boolean(stop.pickUp)) {
          newStopTime = DateTime.fromISO(stop.stopDateTime ?? '');
        }

        if (advanceOneDay) {
          newStopTime = newStopTime?.plus({ day: 1 });
        }

        return {
          operationType: 'Insert' as OperationTypeV4,
          id: newStopId,
          loadId: 0,
          timeZoneOffset: stop.timeZoneOffset,
          stopType: stop.stopType,
          customerId: stop.customerId,
          stopDate: stop.stopDate,
          stopDateFormatted: stop.stopDateFormatted,
          stopDateTimeFormatted: stop.stopDateTimeFormatted,
          description: stop.description,
          address1: stop.address1,
          address2: stop.address2,
          city: stop.city,
          state: stop.state,
          zip: stop.zip,
          locationName: stop.locationName,
          contact: stop.contact,
          phone: stop.phone,
          pickUp: stop.pickUp,
          dropOff: stop.dropOff,
          stopOrder: stop.stopOrder,
          origin: stop.origin,
          destination: stop.destination,
          createdDate: new Date(),
          modifiedDate: undefined,
          // The pickup number should not be copied over.
          referenceNum: undefined,
          time: stop.time,
          instructions: stop.instructions,
          directions: stop.directions,
          driverInTime: load.mode === 'LTL' ? stop.driverInTime : undefined,
          driverOutTime: load.mode === 'LTL' ? stop.driverOutTime : undefined,
          email: stop.email,
          stopCode: stop.stopCode,
          countryCode: stop.countryCode,
          arrived: undefined,
          lat: stop.lat,
          long: stop.long,
          practicalMileageToNextStop: stop.practicalMileageToNextStop,
          shortestMileageToNextStop: stop.shortestMileageToNextStop,
          stopDateTime: newStopTime?.toISO() ?? undefined,
          driverIn: undefined,
          driverOut: undefined,
          timeComments: stop.timeComments,
          needsAppointment: false,
          appointmentDate: undefined,
          appointmentNum: stop.appointmentNum,
          appointment: stop.appointment,
          beginTimeRange: stop.beginTimeRange,
          endTimeRange: stop.endTimeRange,
          ...(load.mode === 'TL' &&
          isFeatureFlagEnabled(FeatureFlags.AddressBookFacilityDetails)()
            ? copiedAppointmentFields(stop)
            : {}),
          facilityDetails:
            load.mode === 'TL'
              ? cloneStopFacilityDetails(stop.facilityDetails)
              : undefined,
          facilityAppointmentType:
            load.mode === 'TL'
              ? (stop.facilityAppointmentType ??
                stop.facilityDetails?.appointmentType)
              : undefined,
          facilitySchedulingMethod:
            load.mode === 'TL'
              ? (stop.facilitySchedulingMethod ??
                stop.facilityDetails?.schedulingMethod)
              : undefined,
        };
      }),
    items: load.items
      ?.filter((item) => item.operationType !== 'Delete')
      .map((item) => {
        const stops =
          load.stops?.filter((stop) => stop.operationType !== 'Delete') || [];
        newItemId--;
        //NOTE: Adding -1 because in some cases pickUpId comes null for a order so findIndex returns -1, then -1 +1 =0, 0*-1=0 which causes the issue with foreign key. By default the first stop will be set as pickup. This is consistent with how new loads are created.
        const pickUpId =
          -1 * (stops.findIndex((stop) => stop.id === item.pickUpId) + 1) || -1;
        const dropOffId =
          -1 * (stops.findIndex((stop) => stop.id === item.dropOffId) + 1);
        return {
          operationType: 'Insert' as OperationTypeV4,
          id: newItemId,
          orderId: 0,
          pickUpId: pickUpId,
          dropOffId: dropOffId,
          loadId: 0,
          nmfc: item.nmfc,
          class: item.class,
          item: item.item,
          description: item.description,
          quantity: item.quantity,
          charge: item.charge,
          weight: item.weight,
          uom: item.uom,
          volume: item.volume,
          hazmat: item.hazmat,
          heightInch: item.heightInch,
          lengthInch: item.lengthInch,
          widthInch: item.widthInch,
          height: item.height,
          length: item.length,
          width: item.width,
          pieces: item.pieces,
          descriptionWithParanthesis: item.descriptionWithParanthesis,
          displayDimensions: item.displayDimensions,
          loadItemHazmatDetail: item.loadItemHazmatDetail,
        };
      }),
  };
};

export const buildLoadFromQuote = (
  quote: TempQuoteModel,
  address?: Address,
  originSnapshot?: LTLAddressBookSnapshot,
  destinationSnapshot?: LTLAddressBookSnapshot,
): IOrderViewModel => {
  const load = BuildOrderFromQuote(
    quote,
    address,
    originSnapshot,
    destinationSnapshot,
  );
  return load;
};

export const addCustomerPayLineItem = (
  pricingQuote: FalveyInsuranceQuoteInLoad,
) => {
  //remove lineItem if already present
  const lineItems = [...orderStore.order.lineItems].filter(
    (lineItem) => lineItem.type !== 'LTL Insurance',
  );
  const insuranceLineItem = [...orderStore.order.lineItems].filter(
    (lineItem) => lineItem.type === 'LTL Insurance',
  );
  if (insuranceLineItem.length > 0) {
    const updatedLineItem = {
      ...insuranceLineItem[0],
      operationType: 'Update' as OperationTypeV4,
      rate: pricingQuote.quotePremium + pricingQuote.quoteServiceFee,
    };
    lineItems.unshift(updatedLineItem);
  } else {
    lineItems.unshift({
      operationType: 'Insert' as OperationTypeV4,
      quantity: 1,
      type: 'LTL Insurance',
      description: 'Shipment Insurance',
      rate: pricingQuote.quotePremium + pricingQuote.quoteServiceFee,
      id: 0,
      settledDate: null,
      createdDate: new Date(),
      status: 'Unsettled',
      referenceNumber: '',
      estimate: false,
      readOnly: false,
      synched: false,
      adjustment: false,
      stopId: null,
      descriptionWithParanthesis: '',
      rateDetail: '',
    });
  }
  updateOrderState({ lineItems });
};

export const addVendorPayLineItem = (
  pricingQuote: FalveyInsuranceQuoteInLoad,
  declaredValue: number,
) => {
  const falveyVendorIndex = (orderStore.order.vendors ?? []).findIndex(
    (item) => item.name === 'Falvey Shippers Insurance',
  );
  const vendors = [...(orderStore.order.vendors || [])];
  if (falveyVendorIndex !== -1) {
    vendors.splice(falveyVendorIndex, 1);
    setOrderStore('order', 'vendors', vendors);
  }
  addNewVendorToOrder({
    ...(falveyVendorInitialState as VendorContractViewModel),
    description: `Insured Value: ${formatAmount(declaredValue)}`,
  });
  const vendorLineItems = orderStore.order.vendors
    ? [
        ...(orderStore.order.vendors[orderStore.activeTab.index]
          .lineItems as VendorLineItem[]),
      ]
    : [];

  const insertVendorObj: VendorLineItem = {
    operationType: 'Insert',
    type: 'Accessorial',
    quantity: 1,
    description: 'Certificate #',
    rate: pricingQuote.quotePremium,
    id: 0,
    vendorContractId: 0,
    settledDate: null,
    createdDate: new Date(),
    status: 'Unsettled',
    referenceNumber: '',
    estimate: false,
    readOnly: false,
    synched: false,
    adjustment: false,
    stopId: 0,
    descriptionWithParanthesis: '',
    rateDetail: '',
  };
  vendorLineItems.push(insertVendorObj);
  updateVendorPropertyAtIndex({ lineItems: vendorLineItems });
};

const updateCustomerPayLineItem = (
  pricingQuote: FalveyInsuranceQuoteInLoad,
) => {
  const lineItems = [...orderStore.order.lineItems];
  const falveyCPIndex = lineItems.findIndex(
    (lineItem) =>
      lineItem.description === 'Shipment Insurance' &&
      lineItem.operationType !== 'Delete',
  );

  if (falveyCPIndex !== -1) {
    const { quotePremium, quoteServiceFee } = pricingQuote;
    const updatedLineItem = {
      ...lineItems[falveyCPIndex],
      rate: quotePremium + quoteServiceFee,
      operationType: (lineItems[falveyCPIndex].id > 0
        ? 'Update'
        : 'Insert') as OperationTypeV4,
    };

    lineItems[falveyCPIndex] = updatedLineItem;
    updateOrderState({ lineItems });
  }
};

export const updateVendorPayLineItem = (
  pricingQuote: FalveyInsuranceQuoteInLoad,
  declaredValue: number,
) => {
  if (
    orderStore.order.vendors !== undefined &&
    size(orderStore.order.vendors) > 0
  ) {
    const falveyVendorIndex = orderStore.order.vendors.findIndex(
      (v) => v.name === 'Falvey Shippers Insurance',
    );
    const vendorLineItems = [
      ...(orderStore.order.vendors[falveyVendorIndex]
        .lineItems as VendorLineItem[]),
    ];

    const updatedVendorLineItems = vendorLineItems.reduce(
      (acc, lineItem) => {
        if (lineItem.description === 'Certificate #') {
          acc.push({
            ...lineItem,
            rate: pricingQuote.quotePremium,
            operationType: lineItem.id > 0 ? 'Update' : 'Insert',
          });
        } else {
          acc.push(lineItem);
        }
        return acc;
      },
      [] as typeof vendorLineItems,
    );
    updateVendorPropertyAtIndex(
      {
        lineItems: updatedVendorLineItems,
        description: `Insured Value: ${formatAmount(declaredValue)}`,
      },
      falveyVendorIndex,
    );
  }
};

export const updateCustomerAndVendorPay = (
  pricingQuote: FalveyInsuranceQuoteInLoad,
  type: string,
  declaredValue: number,
) => {
  if (type === 'insert') {
    addCustomerPayLineItem(pricingQuote);
    addVendorPayLineItem(pricingQuote, declaredValue);
  } else {
    updateCustomerPayLineItem(pricingQuote);
    updateVendorPayLineItem(pricingQuote, declaredValue);
  }
};

export const isFalveyInsuranceCommitted = () => {
  return (
    orderStore.order.comments !== undefined &&
    orderStore.order.comments.some((comment) =>
      comment.comment?.includes('Falvey COI Link:'),
    )
  );
};

export function getTimeZoneAbbreviation(timeZoneId: string): string {
  if (!Boolean(timeZoneId)) {
    return '';
  }

  const options: Intl.DateTimeFormatOptions = {
    timeZone: timeZoneId,
    timeZoneName: 'short',
  };

  const date = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);
  const timeZoneName = parts.find(
    (part) => part.type === 'timeZoneName',
  )?.value;

  if (!!timeZoneName) {
    return timeZoneName;
  }

  throw new Error(
    `Unable to determine time zone abbreviation for ${timeZoneId}`,
  );
}

export const validLoadStatuses = [
  'Assigned',
  'Dispatched',
  'At Shipper',
  'Loaded',
  'In Transit',
  'Out on Delivery',
  'At Receiver',
];

const canShowCustomerDetails = (status: string | undefined): boolean => {
  return validLoadStatuses.includes(status ?? '');
};

export const copyDriverDetailsClipboard = () => {
  const currentLoad = load();

  const stringToCopy = `Carrier Name: ${currentLoad?.carrierName ?? ''}
Driver Name: ${currentLoad?.driver ?? ''}
Secondary Driver Name: ${currentLoad?.secondDriver ?? ''}
Driver Phone: ${currentLoad?.driverPhoneNumber ? formatPhoneNumber(currentLoad.driverPhoneNumber) : ''}
Truck Number: ${currentLoad?.truckNumber ?? ''}
Trailer Number: ${currentLoad?.trailerNumber ?? ''}
VIN Number: ${currentLoad?.vinNumber ?? ''}`;

  navigator.clipboard
    .writeText(stringToCopy)
    .then(() => {
      handleToast(ToastType.Success, 'Details copied to clipboard.');
    })
    .catch(() => {
      handleToast(
        ToastType.Error,
        'Failed to copy details. Please check your browser permissions.',
      );
    });
};

export const copyLoadClipboard = () => {
  // Helper function to conditionally add fields
  const addField = (label: string, value: string | null | undefined) => {
    return value ? `${label}: ${value}` : null;
  };

  const currentLoad = load();
  const minTemperature = currentLoad?.minTemperature ?? '';
  const maxTemperature = currentLoad?.maxTemperature ?? '';
  let temperatureUnit = currentLoad?.temperatureUnit ?? '';
  if (temperatureUnit === 'Fahrenheit') {
    temperatureUnit = 'F';
  } else if (temperatureUnit === 'Celsius') {
    temperatureUnit = 'C';
  }
  const temperatureInstructions =
    minTemperature !== '' && maxTemperature !== ''
      ? `${minTemperature} - ${maxTemperature}°${temperatureUnit} (Run Mode: ${currentLoad?.runMode ?? ''}) ${currentLoad?.tempInstructions ?? ''}`
      : (currentLoad?.tempInstructions ?? '');

  const formatted = [
    `Load Number: ${load()?.loadNum ?? ''}`,
    `Load Status: ${load()?.status}`,
    canShowCustomerDetails(load()?.status)
      ? addField('Reference Number', orderStore.order.referenceNumber ?? '')
      : null,
    addField('Carrier', load()?.carrierName),
    addField('Driver Name', load()?.driver),
    addField(
      'Driver Phone',
      load()?.driverPhoneNumber
        ? formatPhoneNumber(load()?.driverPhoneNumber ?? '')
        : null,
    ),
    addField('Load Equipment', load()?.equipment),
    addField('Temperature Instructions', temperatureInstructions),
    addField('Expected Weight', load()?.weightString),
  ]
    .filter(Boolean)
    .join('\n');
  const stopsFormatted = [
    ...stops().map((stop, i) => {
      const date = stop.stopDateTimeFormatted ?? 'N/A';
      const start = stop.beginTimeRange ?? '';
      const end = stop.endTimeRange ?? '';
      const timeZone = stop.timezone ?? '';

      const timeRange = start && end ? `${start}-${end} ${timeZone}` : '';
      let timeValue;
      if (timeRange || stop.timeComments) {
        timeValue = `${timeRange} ${stop.timeComments ?? ''}`.trim();
      } else {
        timeValue = stop.time ?? '';
      }

      return `Stop #${i + 1}
  Type: ${stop.stopType}
  Address: ${stop.city}, ${stop.state} ${stop.zip} ${stop.countryCode}
  Stop Date: ${date} ${timeValue ? `\n  Time: ${timeValue}` : ''}`;
    }),
  ]
    .filter(Boolean)
    .join('\n');
  const finalFormatted = `${formatted}\n\n${stopsFormatted}`;
  navigator.clipboard
    .writeText(finalFormatted)
    .then(() => {
      handleToast(ToastType.Success, 'Details copied to clipboard.');
    })
    .catch(() => {
      handleToast(
        ToastType.Error,
        'Failed to copy details. Please check your browser permissions.',
      );
    });
};
